import { FC, useEffect, useState } from "react";
import {
  formatDateText,
  getCurrentYear,
  getDaysDiff,
  getNextWeekLastDate,
  getWeekFirstDate,
  getWeekLastDate,
  isDateBetween,
  isDayAfter,
  isDayEnabled,
  isDaySameBefore,
  isMonthEnabled,
  isYearEnabled,
  parseLocalDate,
} from "../../../helpers/datetime";
import { TCalendarType, TDateType } from "./calendar_types";
import { months, monthShortName } from "../../../constants/calendar";
import CustomDropdown from "./calender-dropdown";

const CalendarDayWidget: FC<{
  date: string;
  onSelectDate: (date: string, dateType?: TDateType, toDate?: string) => void;
  calendarMode: TCalendarType;
  toDate?: string;
  label?: string;
  dateType?: TDateType;
  hideMargin?: boolean;
  isSingleDate?: boolean;
  maxDate?: string;
  allYears?: boolean;
  minDate?: string;
}> = ({
  date,
  onSelectDate,
  calendarMode,
  toDate = "",
  label = "From",
  dateType = "FROM",
  hideMargin = false,
  isSingleDate = false,
  maxDate = "",
  allYears = false,
  minDate = "",
}) => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const [dates, setDates] = useState<string[]>([]);
  const [dateNum, setDateNum] = useState<number[]>([]);
  const [toDateNum, setToDateNum] = useState<number[]>([]);

  const [selectedDate, setSelectedDate] = useState(date);
  const [selectedToDate, setSelectedToDate] = useState(toDate);

  const [isCustom, setIsCustom] = useState(false);

  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    setSelectedDate(date);
    setSelectedToDate(toDate);
    if (calendarMode == "WEEKLY" || calendarMode == "CUSTOM") {
      _generateDates();
    }
    if (calendarMode == "WEEKLY" && getDaysDiff(date, toDate) != 7) {
      setIsCustom(true);
    }
  }, [calendarMode, date, toDate]);

  useEffect(() => {
    _generateDates();
    _generateMonths();
  }, [date]);

  useEffect(() => {
    if (isSingleDate) {
      _generateDates();
      _generateMonths();
    }
  }, [selectedDate]);

  const _generateDates = () => {
    let now = parseLocalDate(date);
    if (isSingleDate) {
      now = parseLocalDate(selectedDate);
    }
    const year = now.getFullYear();
    const month = now.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const totalDays = new Date(year, month + 1, 0).getDate();
    const startDay = firstDayOfMonth.getDay(); // Sunday = 0

    const dateGrid = [];
    const dateTexts = [];
    const days = [];
    const toDays = [];

    // Fill in empty slots before the 1st
    for (let i = 0; i < startDay; i++) {
      dateGrid.push("");
    }

    // Add all days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const formatted = formatDateText(date); // YYYY-MM-DD
      if (_isDayEnabled(formatted)) {
        if (calendarMode == "WEEKLY") {
          // If From date is before the To Date
          if (isDaySameBefore(formatted, toDate)) {
            days.push(day);
          }
          // If To date is after the From Date
          if (isDayAfter(formatted, selectedDate)) {
            toDays.push(day);
          }
        } else if (calendarMode == "CUSTOM") {
          // If From date is before the To Date
          if (dateType == "FROM" && isDaySameBefore(formatted, toDate)) {
            days.push(day);
          }
          // If To date is after the From Date
          if (dateType == "TO" && isDayAfter(formatted, toDate)) {
            days.push(day);
          }
        } else {
          days.push(day);
        }
      }

      dateGrid.push(formatted);
      dateTexts.push(day);
    }
    setDates(dateGrid);
    setDateNum(days);
    setToDateNum(toDays);
  };

  // const _generateMonths = () => {
  //   let now = new Date(date);
  //   if (isSingleDate) {
  //     now = new Date(selectedDate);
  //   }
  //   const year = now.getFullYear();
  //   const monthNames = [];

  //   for (let m = 0; m < monthShortName.length; m++) {
  //     if (isMonthEnabled(m) || isYearEnabled(year)) {
  //       monthNames.push(monthShortName[m]);
  //     }
  //   }
  //   setMonths(monthNames);
  // };

  const _generateMonths = () => {
    const now = parseLocalDate(isSingleDate ? selectedDate : date);
    const year = now.getFullYear();

    const monthNames = monthShortName.filter((_, m) => {
      const minDateObj = minDate ? new Date(minDate) : null;
      const maxDateObj = maxDate ? new Date(maxDate) : null;

      if (minDateObj && year < minDateObj.getFullYear()) return false;
      if (maxDateObj && year > maxDateObj.getFullYear()) return false;

      if (
        minDateObj &&
        year === minDateObj.getFullYear() &&
        m < minDateObj.getMonth()
      ) {
        return false;
      }
      if (
        maxDateObj &&
        year === maxDateObj.getFullYear() &&
        m > maxDateObj.getMonth()
      ) {
        return false;
      }

      if (!minDate && !maxDate) {
        return isMonthEnabled(m) || isYearEnabled(year);
      }

      return true;
    });

    setMonths(monthNames);
  };
  let containerWidth = "w-full";
  if (calendarMode != "ALL" && calendarMode != "CUSTOM" && !hideMargin) {
    containerWidth = "w-[60%] mx-auto";
  }

  const _isDaysSelected = (date: string) => {
    let isSelected = false;
    if (selectedDate) {
      isSelected = date == selectedDate;
    }
    if (calendarMode === "WEEKLY") {
      if (isCustom && selectedDate && selectedToDate) {
        isSelected = date === selectedDate || date === selectedToDate;
      } else if (selectedToDate) {
        const firstDate = getWeekFirstDate(selectedToDate);
        const lastDate = getWeekLastDate(selectedToDate);
        isSelected = date === firstDate || date === lastDate;
      }
    }

    // if (calendarMode == "WEEKLY") {
    //
    //   if (isCustom) {
    //     isSelected = isDateBetween(selectedDate, selectedToDate, date);
    //   } else {
    //     let lastDate = getWeekLastDate(date);
    //
    //     isSelected = lastDate == selectedToDate;
    //   }
    // }
    return isSelected;
  };

  const _getDay = () => {
    return parseLocalDate(selectedDate).getDate();
  };

  const _getToDay = () => {
    return parseLocalDate(selectedToDate).getDate();
  };

  const _getMonthNum = () => {
    let month = parseLocalDate(selectedDate).getMonth();
    return month;
  };

  const _getMonth = () => {
    let month = parseLocalDate(selectedDate).getMonth();
    return monthShortName[month];
  };

  const _getYear = () => {
    return parseLocalDate(selectedDate).getFullYear();
  };

  // const _getYears = () => {
  //   let year = getCurrentYear(maxDate);
  //   let yearsList = Array.from({ length: year - 2021 }, (_, i) => year - i);
  //   return yearsList;
  // };

  const _getYears = () => {
    const currentYear = new Date().getFullYear();

    if (allYears) {
      return Array.from(
        { length: currentYear - 1900 + 1 },
        (_, i) => currentYear - i,
      );
    }

    // if mindate
    if (minDate) {
      const minYear = new Date(minDate).getFullYear();
      const maxYear = maxDate
        ? new Date(maxDate).getFullYear()
        : currentYear + 1;
      const length = maxYear - minYear + 1;
      return Array.from(
        { length: length > 0 ? length : 1 },
        (_, i) => maxYear - i,
      );
    }

    const year = getCurrentYear(maxDate);
    return Array.from({ length: year - 2021 }, (_, i) => year - i);
  };
  const _getBgColor = (date: string) => {
    let bgColor = "";
    if (calendarMode == "WEEKLY") {
      let firstDay = getWeekFirstDate(date);
      let lastDay = getWeekLastDate(date);
      if (isCustom) {
        if (isDateBetween(selectedDate, selectedToDate, date)) {
          bgColor += "bg-light-primary";
          if (dateType == "FROM" && selectedDate == date) {
            bgColor += " rounded-l-full";
          }
          if (selectedToDate == date) {
            bgColor += " rounded-r-full";
          }
        }
      } else {
        if (
          isDateBetween(firstDay, lastDay, selectedDate) &&
          isDateBetween(firstDay, lastDay, date)
        ) {
          bgColor += "bg-light-primary";
          if (dateType == "FROM" && firstDay == date) {
            bgColor += " rounded-l-full";
          }
          if (lastDay == date) {
            bgColor += " rounded-r-full";
          }
        }
      }
    } else if (calendarMode == "CUSTOM" || calendarMode == "ALL") {
      if (
        dateType == "FROM" &&
        isDateBetween(selectedDate, selectedToDate, date)
      ) {
        bgColor = "bg-light-primary";
      } else if (
        dateType == "TO" &&
        isDateBetween(selectedToDate, selectedDate, date)
      ) {
        bgColor += "bg-light-primary";
      }
      if (dateType == "FROM" && selectedDate == date) {
        bgColor += " rounded-l-full";
      }
      if (dateType == "TO" && selectedDate == date) {
        bgColor += " rounded-r-full";
      }
    }
    return bgColor;
  };
  const isBeforeMinDate = (checkDate: string) => {
    if (!minDate) return false;
    const checkTime = new Date(checkDate).setHours(0, 0, 0, 0);
    const minTime = new Date(minDate).setHours(0, 0, 0, 0);
    return checkTime < minTime;
  };
  const _isDayEnabled = (date: string) => {
    if (!date) return false;

    if (minDate && isBeforeMinDate(date)) return false;

    if (calendarMode == "CUSTOM") {
      if (dateType == "FROM" && isDaySameBefore(date, toDate)) {
        return true;
      } else if (dateType == "TO" && isDayAfter(date, toDate)) {
        return true;
      }
      return false;
    }
    return date && isDayEnabled(date, maxDate);
  };

  return (
    <div className="p-3">
      {calendarMode != "WEEKLY" && (
        <div className={`flex rounded-lg border mb-2 ${containerWidth}`}>
          <div className="flex flex-col justify-center border-r p-2">
            {calendarMode != "DAILY" && (
              <div className="fs-12 font-bold">{label}</div>
            )}
            <div className="fs-12">DD/MM/YYYY</div>
          </div>
          {!isSingleDate && (
            <CustomDropdown
              label=""
              options={dateNum}
              value=""
              onChange={(d: number) => {
                let date = new Date(_getYear(), _getMonthNum(), d);
                let formattedDate = formatDateText(date);
                setSelectedDate(formattedDate);
                onSelectDate(formattedDate);
              }}
              placeholder={_getDay()}
              className="p-2 border-r"
              isList={true}
              ulHeight={"h-[250px] overflow-y-auto"}
              showBorder={false}
              valueColor="color-blue"
            />
          )}
          <CustomDropdown
            label=""
            options={months}
            value=""
            onChange={(m: string) => {
              const mIndex = monthShortName.indexOf(m);

              let date = new Date(_getYear(), mIndex, _getDay());
              let formattedDate = formatDateText(date);

              setSelectedDate(formattedDate);

              if (!isSingleDate) {
                onSelectDate(formattedDate);
              }
            }}
            placeholder={_getMonth()}
            className="p-2 border-r"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
          <CustomDropdown
            label=""
            options={_getYears()}
            value=""
            onChange={(y: number) => {
              let date = new Date(y, _getMonthNum(), _getDay());
              let formattedDate = formatDateText(date);
              setSelectedDate(formattedDate);
              if (!isSingleDate) {
                onSelectDate(formattedDate);
              }
            }}
            placeholder={_getYear()}
            className="p-2"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
        </div>
      )}
      {calendarMode == "WEEKLY" && (
        <div
          className={`flex items-center rounded-lg border mb-2 ${containerWidth}`}
        >
          <div className="flex-col p-2">
            <div className="fs-12 font-bold">From</div>
          </div>
          <CustomDropdown
            label=""
            options={dateNum}
            value=""
            onChange={(d: number) => {
              let date = new Date(_getYear(), _getMonthNum(), d);
              let formattedDate = formatDateText(date);
              setSelectedDate(formattedDate);
              onSelectDate(formattedDate, "FROM");
              setIsCustom(true);
            }}
            placeholder={_getDay()}
            className="p-2 border-l border-r"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
          <div className="flex-col p-2">
            <div className="fs-12 font-bold">To</div>
          </div>
          <CustomDropdown
            label=""
            options={toDateNum}
            value=""
            onChange={(d: number) => {
              let date = new Date(_getYear(), _getMonthNum(), d);
              let formattedDate = formatDateText(date);
              setSelectedToDate(formattedDate);
              onSelectDate(formattedDate, "TO");
              setIsCustom(true);
            }}
            placeholder={_getToDay()}
            className="p-2 border-l border-r"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
          <CustomDropdown
            label=""
            options={months}
            value=""
            onChange={(m: string) => {
              let mIndex = months.findIndex((month) => month == m);
              let date = new Date(_getYear(), mIndex, _getDay());
              let formattedDate = formatDateText(date);

              let firstDay = getWeekFirstDate(formattedDate);
              let lastDay = getWeekLastDate(formattedDate);

              setSelectedDate(firstDay);
              setSelectedToDate(lastDay);

              onSelectDate(firstDay, "MONTH", lastDay);
              setIsCustom(true);
            }}
            placeholder={_getMonth()}
            className="p-2 border-r"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
          <CustomDropdown
            label=""
            options={_getYears()}
            value=""
            onChange={(y: number) => {
              let date = new Date(y, _getMonthNum(), _getDay());
              let formattedDate = formatDateText(date);

              let firstDay = getWeekFirstDate(formattedDate);
              let lastDay = getWeekLastDate(formattedDate);
              setSelectedDate(firstDay);
              setSelectedToDate(lastDay);

              onSelectDate(firstDay, "YEAR", lastDay);
              setIsCustom(true);
            }}
            placeholder={_getYear()}
            className="p-2"
            isList={true}
            ulHeight={"h-[250px] overflow-y-auto"}
            showBorder={false}
            valueColor="color-blue"
          />
        </div>
      )}
      <div
        className={`grid grid-cols-7 gap-2 text-center fs-14 ${containerWidth}`}
      >
        {days.map((day) => (
          <div key={day} className="h-8 w-8">
            {day}
          </div>
        ))}
      </div>
      <div
        className={`grid grid-cols-7 mt-2 text-center fs-14 ${containerWidth}`}
      >
        {dates.map((date, i) => (
          <div
            key={i}
            className={`h-8 mb-1 flex items-center justify-center 
                                ${
                                  _isDayEnabled(date)
                                    ? "cursor-pointer"
                                    : "color_inactive opacity-50 pointer-events-none"
                                } 
                ${_getBgColor(date)} 
              `}
            onClick={() => {
              if (date && _isDayEnabled(date)) {
                if (calendarMode == "CUSTOM") {
                  if (dateType == "FROM" && isDaySameBefore(date, toDate)) {
                    setSelectedDate(date);
                    onSelectDate(date);
                  } else if (dateType == "TO" && isDayAfter(date, toDate)) {
                    setSelectedDate(date);
                    onSelectDate(date);
                  }
                } else {
                  setSelectedDate(date);
                  onSelectDate(date);
                  if (calendarMode == "WEEKLY") {
                    setSelectedDate(getWeekFirstDate(date));
                    setSelectedToDate(getWeekLastDate(date));
                    setIsCustom(false);
                  }
                }
              }
            }}
          >
            <span
              className={`w-8 h-8 flex flex-col items-center justify-center fs-12 ${
                _isDaysSelected(date) ? "br-50 bg-primary text-white" : ""
              }`}
            >
              {date ? parseLocalDate(date).getDate() : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarDayWidget;
